import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import useForm from '../../hooks/useForm';
import { getDashboardPath } from '../../utils/auth';
import logo from '../../assets/images/Bholi ko itahari .png';

const validate = (values) => {
  const errors = {};
  if (!values.name || values.name.trim().length < 2) errors.name = 'Name must be at least 2 characters';
  if (!values.email || !/\S+@\S+\.\S+/.test(values.email)) errors.email = 'Enter a valid email address';
  if (!values.password || values.password.length < 6) errors.password = 'Password must be at least 6 characters';
  if (values.confirm !== values.password) errors.confirm = 'Passwords do not match';
  return errors;
};

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#F5F3EE',
    padding: '2rem 1rem',
  },
  card: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    maxWidth: '900px',
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
  },

  // ── Left panel ──
  leftPanel: {
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoImg: {
    width: '200px',
    height: 'auto',
    display: 'block',
  },

  // ── Right panel ──
  rightPanel: {
    background: '#0a5c2e',
    padding: '3rem 2.5rem',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  formWrap: {
    maxWidth: '360px',
    width: '100%',
    margin: '0 auto',
  },
  formTitle: {
    fontSize: '1.55rem',
    fontWeight: 700,
    color: '#ffffff',
    margin: '0 0 0.4rem 0',
  },
  formSub: {
    fontSize: '0.875rem',
    color: 'rgba(255,255,255,0.7)',
    margin: '0 0 1.75rem 0',
  },

  // ── Alert ──
  alert: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    fontSize: '0.85rem',
    marginBottom: '1rem',
    background: '#FEE2E2',
    color: '#991B1B',
    border: '1px solid #FECACA',
  },

  // ── Form group ──
  formGroup: {
    marginBottom: '1rem',
  },
  label: {
    display: 'block',
    fontSize: '0.82rem',
    fontWeight: 600,
    color: 'rgba(255,255,255,0.85)',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.65rem 0.85rem',
    fontSize: '0.875rem',
    border: '1.5px solid rgba(255,255,255,0.3)',
    borderRadius: '8px',
    color: '#111',
    background: '#ffffff',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  errorMsg: {
    display: 'block',
    fontSize: '0.78rem',
    color: '#DC2626',
    marginTop: '0.3rem',
  },

  // ── Submit button ──
  btn: {
    width: '100%',
    marginTop: '1.5rem',
    padding: '0.8rem',
    background: '#ffffff',
    color: '#2B6349',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.95rem',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    letterSpacing: '0.01em',
  },
  btnDisabled: {
    opacity: 0.65,
    cursor: 'not-allowed',
  },

  // ── Spinner ──
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },

  // ── Footer text ──
  footerText: {
    textAlign: 'center',
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
    marginTop: '1.25rem',
  },
  footerLink: {
    color: '#ffffff',
    fontWeight: 700,
    textDecoration: 'underline',
  },
};

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState('');
  const [loading, setLoading]         = useState(false);
  const [selectedRole, setSelectedRole] = useState('public');

  const { values, errors, touched, handleChange, handleBlur, validateAll } = useForm(
    { name: '', email: '', password: '', confirm: '' },
    validate
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    if (!validateAll()) return;
    setLoading(true);
    try {
      const newUser = await register(values.name, values.email, values.password, selectedRole);
      const destination = getDashboardPath(newUser?.role);
      navigate(destination, { replace: true });
    } catch (err) {
      setServerError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { id: 'name',     label: 'Full Name',        type: 'text',     placeholder: 'Your full name',  autoComplete: 'name' },
    { id: 'email',    label: 'Email Address',    type: 'email',    placeholder: 'you@example.com', autoComplete: 'email' },
    { id: 'password', label: 'Password',         type: 'password', placeholder: '6+ characters',   autoComplete: 'new-password' },
    { id: 'confirm',  label: 'Confirm Password', type: 'password', placeholder: 'Repeat password', autoComplete: 'new-password' },
  ];

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={S.page}>
        <div style={S.card}>

          {/* ── Left panel ── */}
          <div style={S.leftPanel}>
            <img src={logo} alt="Bholi ko itahari" style={S.logoImg} />
          </div>

          {/* ── Right panel ── */}
          <div style={S.rightPanel}>
            <div style={S.formWrap}>
              <h2 style={S.formTitle}>Create account</h2>
              <p style={S.formSub}>Join the Vision 2050 community</p>

              {serverError && (
                <div style={S.alert} role="alert">{serverError}</div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {fields.map(({ id, label, type, placeholder, autoComplete }) => (
                  <div style={S.formGroup} key={id}>
                    <label style={S.label} htmlFor={id}>{label}</label>
                    <input
                      id={id}
                      name={id}
                      type={type}
                      style={{
                        ...S.input,
                        ...(touched[id] && errors[id] ? S.inputError : {}),
                      }}
                      placeholder={placeholder}
                      value={values[id]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete={autoComplete}
                    />
                    {touched[id] && errors[id] && (
                      <span style={S.errorMsg} role="alert">{errors[id]}</span>
                    )}
                  </div>
                ))}

                {/* Role Selector
                <div style={S.formGroup}>
                  <label style={S.label} htmlFor="role">Register as</label>
                  <select
                    id="role"
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    style={S.input}
                  >
                    <option value="reporter">Reporter</option>
                  </select>
                </div> */}

                <button
                  type="submit"
                  style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
                  disabled={loading}
                >
                  {loading ? (
                    <><div style={S.spinner} />Creating account…</>
                  ) : 'Create Account'}
                </button>
              </form>

              <p style={S.footerText}>
                Already have an account?{' '}
                <Link to="/login" style={S.footerLink}>Sign in</Link>
              </p>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default Register;